import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthErrorScreen, AuthLoadingScreen } from '@/auth/AuthErrorScreen'
import { useAuth } from '@/auth/AuthContext'
import { resolveRegistrationEmail } from '@/auth/firebase'
import { consumeAuthRedirectPath } from '@/auth/storage'
import { TurnstileWidget } from '@/components/TurnstileWidget'
import { env } from '@/config/env'
import { getLocale, toIntlLocale } from '@/config/locale'
import { useTranslation } from '@/i18n'
import {
  completeRegistration,
  mapRegistrationValidation,
  AuthenticatedUserApiError,
} from '@/features/user/api'
import {
  useRegistrationCountries,
  useRegistrationLeagues,
  useRegistrationTeams,
  type RegistrationCountry,
  type RegistrationLeague,
  type RegistrationTeam,
} from '@/features/registration/api'

const TERMS_URLS: Record<string, string> = {
  nl: 'https://golero.nl/nl/algemene-voorwaarden',
  en: 'https://golero.nl/en/terms-of-service',
  fr: 'https://golero.nl/fr/conditions-generales',
  de: 'https://golero.nl/de/nutzungsbedingungen',
}

export function CompleteProfilePage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const locale = getLocale()
  const intlLocale = toIntlLocale(locale)
  const { authState, loading, user, refreshAuthState } = useAuth()
  const [step, setStep] = useState(0)
  const [displayName, setDisplayName] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [displayNameError, setDisplayNameError] = useState('')
  const [generalError, setGeneralError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState<RegistrationCountry | null>(null)
  const [selectedLeague, setSelectedLeague] = useState<RegistrationLeague | null>(null)
  const [selectedTeam, setSelectedTeam] = useState<RegistrationTeam | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileError, setTurnstileError] = useState('')
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const [showCompletion, setShowCompletion] = useState(false)

  const countriesQuery = useRegistrationCountries()
  const leaguesQuery = useRegistrationLeagues(selectedCountry?.id ?? null)
  const teamsQuery = useRegistrationTeams(selectedLeague?.id ?? null)
  const turnstileSiteKey = env.VITE_TURNSTILE_SITE_KEY
  const turnstileEnabled = Boolean(turnstileSiteKey)

  useEffect(() => {
    if (loading) return

    if (authState === 'ready') {
      if (showCompletion) return
      navigate(consumeAuthRedirectPath('/dashboard'), { replace: true })
      return
    }

    if (authState === 'verifyEmail') {
      navigate('/verifieer-email', { replace: true })
      return
    }

    if (authState === 'notLoggedIn') {
      navigate('/login', { replace: true })
    }
  }, [authState, loading, navigate, showCompletion])

  useEffect(() => {
    if (!showCompletion) return

    const timeoutId = window.setTimeout(() => {
      navigate(consumeAuthRedirectPath('/dashboard'), { replace: true })
    }, 1900)

    return () => window.clearTimeout(timeoutId)
  }, [navigate, showCompletion])

  const termsUrl = TERMS_URLS[locale] ?? TERMS_URLS.en
  const selectedLanguageLabel = getLocaleLabel(intlLocale, locale)

  async function handleNext() {
    setGeneralError('')

    if (step === 0) {
      if (displayName.trim().length < 2) {
        setDisplayNameError(t('Kies een naam van minimaal 2 tekens.'))
        return
      }

      setDisplayNameError('')
      setStep(1)
      return
    }

    if (step === 1) {
      setStep(2)
      return
    }

    await handleSubmit()
  }

  function handleBack() {
    setGeneralError('')
    setDisplayNameError('')
    setStep((current) => Math.max(0, current - 1))
  }

  function resetTurnstileChallenge() {
    if (!turnstileEnabled) return
    setTurnstileToken(null)
    setTurnstileResetKey((current) => current + 1)
  }

  async function handleSubmit() {
    if (!user) return
    if (!acceptedTerms) {
      setGeneralError(t('Accepteer eerst de voorwaarden om door te gaan.'))
      return
    }
    if (turnstileEnabled && !turnstileToken) {
      setGeneralError(turnstileError || t('Rond eerst de anti-bot controle af.'))
      return
    }

    setSubmitting(true)
    setGeneralError('')

    try {
      const email = await resolveRegistrationEmail(user)
      if (!email) {
        setGeneralError(t('We konden geen e-mailadres aan je account koppelen. Log opnieuw in.'))
        return
      }

      await completeRegistration({
        email,
        display_name: displayName.trim(),
        favorite_team_id: selectedTeam?.id ?? null,
        preferred_locale: locale,
        accepted_terms: acceptedTerms ? '1' : '0',
        turnstile_token: turnstileEnabled ? (turnstileToken ?? undefined) : undefined,
      })

      setShowCompletion(true)
      await refreshAuthState()
    } catch (submitError) {
      resetTurnstileChallenge()

      const validation = mapRegistrationValidation(submitError)
      if (validation) {
        if (validation.displayNameError) {
          setDisplayNameError(validation.displayNameError)
          setStep(0)
        }
        setGeneralError(validation.message)
        return
      }

      if (submitError instanceof AuthenticatedUserApiError) {
        if (submitError.status === 429) {
          setGeneralError(t('Te veel verzoeken. Probeer het straks opnieuw.'))
          return
        }

        if (submitError.status >= 500) {
          setGeneralError(t('De server is tijdelijk niet beschikbaar.'))
          return
        }

        setGeneralError(submitError.message)
        return
      }

      setGeneralError(
        submitError instanceof Error
          ? submitError.message
          : t('Profiel aanmaken is mislukt.'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  function selectCountry(country: RegistrationCountry) {
    setSelectedCountry(country)
    setSelectedLeague(null)
    setSelectedTeam(null)
  }

  function selectLeague(league: RegistrationLeague) {
    setSelectedLeague(league)
    setSelectedTeam(null)
  }

  if (showCompletion) {
    return <ProfileCompletionScreen displayName={displayName} team={selectedTeam} />
  }

  if (loading) return <AuthLoadingScreen fullscreen={false} />
  if (authState === 'error') return <AuthErrorScreen fullscreen={false} />
  if (authState !== 'needsProfile') return <AuthLoadingScreen fullscreen={false} />

  return (
    <div className="w-full">
      <div className="pb-28 sm:pb-32">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-surface-400">
              {t('Account aanmaken')}
            </p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-white">
              {t('Rond je profiel af')}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400">
              {t('Vul je gegevens in en maak je account compleet.')}
            </p>
          </div>
          <div className="flex gap-2">
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                className={[
                  'h-2.5 w-14 rounded-full transition',
                  index <= step ? 'bg-surface-300' : 'bg-surface-800',
                ].join(' ')}
              />
            ))}
          </div>
        </div>

        <div>
          <section
            className={[
              'rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6',
              step === 1 ? '' : 'mx-auto max-w-2xl',
            ].join(' ')}
          >
            {step === 0 ? (
              <div className="mx-auto max-w-xl text-center">
                <StepHeader
                  eyebrow={t('Stap 1')}
                  title={t('Kies je naam')}
                  description={t('Dit is de naam die in poules, rankings en activiteiten zichtbaar wordt.')}
                />

                <div className="mx-auto mt-6 max-w-sm text-left">
                  <label htmlFor="display-name" className="mb-2 block text-sm text-surface-300">
                    {t('Weergavenaam')}
                  </label>
                  <input
                    id="display-name"
                    type="text"
                    autoComplete="nickname"
                    value={displayName}
                    onChange={(event) => {
                      setDisplayName(event.target.value)
                      if (displayNameError) setDisplayNameError('')
                    }}
                    className="w-full rounded-xl border border-white/[0.08] bg-[#111719] px-4 py-3 text-sm text-white placeholder:text-surface-500 transition focus:border-white/[0.24] focus:outline-none"
                    placeholder={t('Voorbeeldnaam')}
                  />
                  {displayNameError ? (
                    <p className="mt-3 rounded-lg bg-red-950/60 px-4 py-2.5 text-sm text-red-400">
                      {displayNameError}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {step === 1 ? (
              <div className="space-y-6">
                <StepHeader
                  eyebrow={t('Stap 2')}
                  title={t('Kies je favoriete club')}
                  description={t('Deze stap is optioneel. Je kunt hem ook overslaan en later in je profiel aanpassen.')}
                />

                <div className="flex flex-wrap items-center gap-2">
                  <SelectionBreadcrumb
                    label={t('Land')}
                    value={selectedCountry?.name}
                    state={!selectedCountry ? 'active' : 'done'}
                    onClick={() => {
                      setSelectedCountry(null)
                      setSelectedLeague(null)
                      setSelectedTeam(null)
                    }}
                  />
                  <span className="text-surface-700">›</span>
                  <SelectionBreadcrumb
                    label={t('Competitie')}
                    value={selectedLeague?.name}
                    state={!selectedCountry ? 'upcoming' : !selectedLeague ? 'active' : 'done'}
                    onClick={
                      selectedCountry
                        ? () => {
                            setSelectedLeague(null)
                            setSelectedTeam(null)
                          }
                        : undefined
                    }
                  />
                  <span className="text-surface-700">›</span>
                  <SelectionBreadcrumb
                    label={t('Club')}
                    value={selectedTeam?.name}
                    state={!selectedLeague ? 'upcoming' : 'active'}
                    onClick={selectedLeague ? () => setSelectedTeam(null) : undefined}
                  />
                </div>

                {!selectedCountry ? (
                  <SelectionColumn
                    title={t('Land')}
                    subtitle={t('Welke competitie wil je openen?')}
                    loading={countriesQuery.isLoading}
                    error={countriesQuery.isError ? t('Landen laden mislukt.') : ''}
                    items={countriesQuery.data ?? []}
                    selectedId={null}
                    getLabel={(country) => country.name}
                    getImage={(country) => country.image_path ?? undefined}
                    onSelect={(country) => selectCountry(country)}
                  />
                ) : !selectedLeague ? (
                  <SelectionColumn
                    title={t('Competitie')}
                    subtitle={t('Competities in {country}', { country: selectedCountry.name })}
                    loading={leaguesQuery.isLoading}
                    error={leaguesQuery.isError ? t('Competities laden mislukt.') : ''}
                    emptyMessage={t('Geen competities gevonden.')}
                    items={leaguesQuery.data ?? []}
                    selectedId={null}
                    getLabel={(league) => league.name}
                    getImage={(league) => league.image_path ?? undefined}
                    onSelect={(league) => selectLeague(league)}
                  />
                ) : (
                  <SelectionColumn
                    title={t('Club')}
                    subtitle={t('Teams in {league}', { league: selectedLeague.name })}
                    loading={teamsQuery.isLoading}
                    error={teamsQuery.isError ? t('Teams laden mislukt.') : ''}
                    emptyMessage={t('Geen teams gevonden.')}
                    items={teamsQuery.data ?? []}
                    selectedId={selectedTeam?.id ?? null}
                    getLabel={(team) => team.name}
                    getImage={(team) => team.logo_url ?? undefined}
                    onSelect={(team) => setSelectedTeam(team)}
                  />
                )}
              </div>
            ) : null}

            {step === 2 ? (
              <div>
                <StepHeader
                  eyebrow={t('Stap 3')}
                  title={t('Bevestig je profiel')}
                  description={t('Controleer je gegevens en rond je account af.')}
                />

                <div className="mt-6 overflow-hidden rounded-[1.35rem] border border-white/[0.07] bg-[#111719]">
                  <div className="relative px-5 py-8 text-center sm:px-8">
                    <div className="pointer-events-none absolute inset-x-16 top-0 h-28 rounded-full bg-white/[0.03] blur-3xl" />
                    <div className="relative mx-auto flex h-24 w-24 items-center justify-center rounded-full border border-surface-700 bg-surface-950 shadow-[0_18px_40px_rgba(0,0,0,0.28)]">
                      {selectedTeam?.logo_url ? (
                        <img
                          src={selectedTeam.logo_url}
                          alt=""
                          className="h-16 w-16 object-contain"
                        />
                      ) : (
                        <span className="text-4xl" aria-hidden="true">⚽</span>
                      )}
                    </div>
                    <p className="relative mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-surface-500">
                      {t('Je profiel')}
                    </p>
                    <h3 className="relative mt-2 text-3xl font-black tracking-tight text-white">
                      {displayName.trim() || t('Jouw profiel')}
                    </h3>
                    <p className="relative mt-2 text-sm text-surface-400">
                      {selectedTeam
                        ? t('Fan van {team}', { team: selectedTeam.name })
                        : t('Kies later een favoriete club in je profiel.')}
                    </p>
                  </div>

                  <div className="grid border-t border-white/[0.07] sm:grid-cols-2">
                    <ProfileMeta
                      label={t('Weergavenaam')}
                      value={displayName.trim() || t('Nog niet ingevuld')}
                    />
                    <ProfileMeta label={t('Voorkeurstaal')} value={selectedLanguageLabel} />
                  </div>
                </div>

                <label className="mt-5 flex items-start gap-3 rounded-[1.15rem] border border-white/[0.07] bg-[#111719] px-4 py-4 text-sm text-surface-300">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(event) => setAcceptedTerms(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-surface-600 bg-surface-950 accent-[#c4c5c2]"
                  />
                  <span>
                    Ik ga akkoord met de{' '}
                    <button
                      type="button"
                      onClick={() => window.open(termsUrl, '_blank', 'noopener,noreferrer')}
                      className="font-semibold text-surface-200 transition hover:text-white"
                    >
                      {t('voorwaarden')}
                    </button>
                    .
                  </span>
                </label>

                {turnstileSiteKey ? (
                  <div className="mt-5">
                    <TurnstileWidget
                      siteKey={turnstileSiteKey}
                      action="complete-profile"
                      resetKey={turnstileResetKey}
                      onTokenChange={(token) => {
                        setTurnstileToken(token)
                        if (token) {
                          setTurnstileError('')
                          setGeneralError('')
                        }
                      }}
                      onError={(message) => {
                        setTurnstileToken(null)
                        setTurnstileError(message)
                      }}
                    />

                    {turnstileError ? (
                      <p className="mt-3 rounded-lg bg-red-950/60 px-4 py-2.5 text-sm text-red-400">
                        {turnstileError}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            {generalError ? (
              <p className="mt-5 rounded-lg bg-red-950/60 px-4 py-2.5 text-sm text-red-400">
                {generalError}
              </p>
            ) : null}
          </section>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-surface-800/90 bg-surface-950/92 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={handleBack}
            disabled={step === 0 || submitting}
            className="rounded-xl border border-surface-700 px-4 py-3 text-sm font-semibold text-surface-200 transition hover:border-surface-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('Terug')}
          </button>

          <div className="flex gap-3">
            {step === 1 ? (
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={submitting}
                className="rounded-xl px-4 py-3 text-sm font-medium text-surface-400 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('Overslaan')}
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => void handleNext()}
              disabled={submitting}
              className="rounded-xl bg-accent-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t('Opslaan...') : step === 2 ? t('Profiel afronden') : t('Volgende')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function getLocaleLabel(intlLocale: string, locale: string) {
  try {
    return new Intl.DisplayNames([intlLocale], { type: 'language' }).of(locale) ?? locale
  } catch {
    return locale
  }
}

function ProfileCompletionScreen({
  displayName,
  team,
}: {
  displayName: string
  team: RegistrationTeam | null
}) {
  const { t } = useTranslation()

  return (
    <div className="flex min-h-[68vh] items-center justify-center px-4 py-12">
      <div className="animate-profile-completion-in w-full max-w-md text-center">
        <div className="relative mx-auto flex h-28 w-28 items-center justify-center">
          <div className="animate-profile-completion-ring absolute inset-0 rounded-full border border-emerald-300/60" />
          <div className="relative flex h-24 w-24 items-center justify-center rounded-full border border-white/[0.12] bg-[#0a0d0f] shadow-[0_18px_50px_rgba(0,0,0,0.42)]">
            {team?.logo_url ? (
              <img src={team.logo_url} alt="" className="h-16 w-16 object-contain" />
            ) : (
              <span className="text-4xl" aria-hidden="true">⚽</span>
            )}
          </div>
          <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-4 border-[#080b0d] bg-emerald-300 text-sm font-black text-[#07100c]">
            ✓
          </span>
        </div>

        <p className="mt-8 text-xs font-semibold uppercase tracking-[0.22em] text-surface-500">
          {t('Profiel afgerond')}
        </p>
        <h1 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
          {t('Welkom, {name}', { name: displayName.trim() })}
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-surface-400">
          {team ? t('Je staat klaar als fan van {team}.', { team: team.name }) : t('Je profiel staat klaar.')}
        </p>

        <div className="mx-auto mt-8 h-1 max-w-xs overflow-hidden rounded-full bg-surface-800">
          <div className="animate-profile-completion-progress h-full rounded-full bg-emerald-300" />
        </div>
        <p className="mt-3 text-xs text-surface-500">{t('Je gaat naar Home…')}</p>
      </div>
    </div>
  )
}

function ProfileMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-5 py-3.5 first:border-b first:border-white/[0.07] sm:first:border-b-0 sm:odd:border-r sm:odd:border-white/[0.07] sm:px-6">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-surface-500">
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-surface-200">{value}</p>
    </div>
  )
}

function StepHeader({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string
  title: string
  description: string
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-surface-500">
        {eyebrow}
      </p>
      <h2 className="mt-2 text-2xl font-black tracking-tight text-white">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-surface-400">{description}</p>
    </div>
  )
}

function SelectionBreadcrumb({
  label,
  value,
  state,
  onClick,
}: {
  label: string
  value?: string | undefined
  state: 'done' | 'active' | 'upcoming'
  onClick?: (() => void) | undefined
}) {
  const clickable = state !== 'upcoming' && Boolean(onClick)

  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={[
        'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition',
        state === 'active'
          ? 'border-surface-500 bg-surface-800 text-white'
          : state === 'done'
            ? 'border-white/[0.08] bg-[#111719] text-surface-200 hover:border-white/[0.18]'
            : 'cursor-default border-white/[0.06] bg-[#0d1113] text-surface-600',
      ].join(' ')}
    >
      <span className="uppercase tracking-wide">{label}</span>
      {value ? <span className="font-normal text-surface-300">{value}</span> : null}
    </button>
  )
}

function SelectionColumn<T extends { id: number }>({
  title,
  subtitle,
  items,
  selectedId,
  loading,
  error,
  emptyMessage = 'Geen resultaten gevonden.',
  getLabel,
  getImage,
  onSelect,
}: {
  title: string
  subtitle: string
  items: T[]
  selectedId: number | null
  loading: boolean
  error: string
  emptyMessage?: string
  getLabel: (item: T) => string
  getImage: (item: T) => string | undefined
  onSelect: (item: T) => void
}) {
  const { t } = useTranslation()

  return (
    <section className="rounded-[1.35rem] border border-white/[0.07] bg-[#0d1113] p-4">
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      <p className="mt-1 text-xs leading-5 text-surface-500">{subtitle}</p>

      {loading ? <p className="mt-4 text-sm text-surface-400">{t('Laden...')}</p> : null}
      {error ? <p className="mt-4 text-sm text-red-400">{error}</p> : null}
      {!loading && !error && items.length === 0 ? (
        <p className="mt-4 text-sm text-surface-400">{emptyMessage}</p>
      ) : null}

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        {items.map((item) => {
          const active = item.id === selectedId
          const image = getImage(item)

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item)}
              className={[
                'rounded-2xl border px-3 py-5 text-center transition',
                active
                  ? 'border-surface-500 bg-surface-800 text-white'
                  : 'border-white/[0.08] bg-[#111719] text-surface-200 hover:border-white/[0.18]',
              ].join(' ')}
            >
              <div className="flex min-h-[4.5rem] items-center justify-center">
                {image ? (
                  <img src={image} alt="" className="max-h-14 max-w-[4.5rem] object-contain" />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-surface-800" />
                )}
              </div>
              <p className="mt-3 line-clamp-2 text-sm font-medium">{getLabel(item)}</p>
            </button>
          )
        })}
      </div>
    </section>
  )
}
